import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { ref, onValue, update } from "firebase/database";
import { Loader2, ClipboardList, CheckCircle, X, AlertCircle, Printer, CreditCard, QrCode, CheckCircle2, Hash, IndianRupee } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function WorkList() {
  const [works, setWorks] = useState([]);
  const [visibility, setVisibility] = useState({});
  const [selectedWorks, setSelectedWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userPayments, setUserPayments] = useState({});
  const navigate = useNavigate();

  // Challan & Payment States
  const [showChallan, setShowChallan] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [paymentData, setPaymentData] = useState({
    userUpiId: "",
    paidAmount: "250"
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser')) || {};
    const userShareType = (user._node?.includes('EngineerRegistrations') || user.role === 'Engineer') ? 'सुबे' :
      (user._node?.includes('SocietyRegistrations') || user.role === 'Society') ? 'मजूर' : null;

    // Fetch visibility settings from NEW consolidated Meetings node
    const meetingsRef = ref(db, 'ekamwatapzp/Meetings');
    onValue(meetingsRef, (snapshot) => {
      const visMap = { General: 'show' }; // Default General to show
      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.entries(data).forEach(([num, details]) => {
          visMap[num] = details.status || 'hide';
        });
      }
      setVisibility(visMap);
    });

    const worksRef = ref(db, 'ekamwatapzp/WorkNames');
    const unsubscribe = onValue(worksRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        let flattenedWorks = [];

        Object.entries(data).forEach(([key, val]) => {
          if (val && typeof val === 'object') {
            if (val.schemeName) {
              // Direct work object (Flat structure like ZP Head works)
              if (!userShareType || val.sharedwork === userShareType) {
                flattenedWorks.push({
                  id: key,
                  meetingNumber: val.meetingNumber || val.category || "General",
                  ...val
                });
              }
            } else {
              // Folder containing works (Nested structure like Incharge works)
              const meetingNum = key;
              Object.entries(val).forEach(([id, value]) => {
                if (!userShareType || value.sharedwork === userShareType) {
                  flattenedWorks.push({
                    id,
                    meetingNumber: meetingNum,
                    ...value
                  });
                }
              });
            }
          }
        });

        setWorks(flattenedWorks.reverse()); // Latest first
      } else {
        setWorks([]);
      }
      setLoading(false);
    });

    if (user._node && user._key) {
      const paymentsRef = ref(db, `${user._node}/${user._key}/work_payment`);
      onValue(paymentsRef, (snapshot) => {
        if (snapshot.exists()) {
          setUserPayments(snapshot.val());
        }
      });
    }

    return () => unsubscribe();
  }, []);

  const handleWorkToggle = (workId) => {
    setSelectedWorks(prev => {
      if (prev.includes(workId)) {
        return prev.filter(id => id !== workId);
      } else {
        if (prev.length >= 3) {
          alert("तुम्ही फक्त ३ कामे निवडू शकता! (You can only select up to 3 works!)");
          return prev;
        }
        return [...prev, workId];
      }
    });
  };

  const groupedWorks = works.reduce((acc, work) => {
    const meeting = work.meetingNumber;
    // Only include if meeting visibility is "show"
    if (visibility[meeting] === "show") {
      if (!acc[meeting]) acc[meeting] = [];
      acc[meeting].push(work);
    }
    return acc;
  }, {});

  const hasVisibleWorks = Object.keys(groupedWorks).length > 0;

  const handleConfirmSubmit = async () => {
    const selectedData = works.filter(w => selectedWorks.includes(w.id));
    const user = JSON.parse(localStorage.getItem('currentUser')) || {};

    if (user._node && user._key) {
      try {
        setLoading(true);
        const meetingNum = selectedData[0]?.meetingNumber || "General";

        // Group works by meeting if necessary, but following current pattern
        const selectedWorksData = selectedData.map(w => `${w.workNumber} - ${w.workName}`);

        const selectionRef = ref(db, `${user._node}/${user._key}/work_payment/${meetingNum}`);

        await update(selectionRef, {
          selectedWorks: selectedWorksData,
          selectionTimestamp: new Date().toISOString(),
          status: 'selected' // Initial status
        });

        localStorage.setItem('selectedWorksForChallan', JSON.stringify(selectedData));
        setLoading(false);
        setShowChallan(true);
        setShowConfirmModal(false);
      } catch (error) {
        console.error("Error storing selected works:", error);
        setLoading(false);
        alert("कामे सेव्ह करताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा.");
      }
    } else {
      localStorage.setItem('selectedWorksForChallan', JSON.stringify(selectedData));
      setShowChallan(true);
      setShowConfirmModal(false);
    }
  };

  // --- Challan & Payment Logic ---
  const selectedDataFull = works.filter(w => selectedWorks.includes(w.id));
  const meetingNumForChallan = selectedDataFull[0]?.meetingNumber || "General";
  const userPaymentInfo = userPayments[meetingNumForChallan];
  const isPaid = userPaymentInfo?.status === 'completed' || paymentDone;

  useEffect(() => {
    if (userPaymentInfo && !invoiceData) {
      setInvoiceData(userPaymentInfo);
    }
  }, [userPaymentInfo]);

  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const challanNo = `ZP${new Date().getFullYear()}${Math.floor(100000 + Math.random() * 900000)}`;
  const upiId = "sangramshinde7007@okhdfcbank";
  const amount = 250;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(`upi://pay?pa=${upiId}&pn=Zilla%20Parishad%20Sangli&am=${amount}&cu=INR&tn=Work%20Selection%20Fee`)}`;

  const userForChallan = JSON.parse(localStorage.getItem('currentUser')) || {};
  const isSociety = userForChallan?._node?.includes('Society');
  const userName = isSociety ? (userForChallan?.societyName || "संस्थेचे नाव") : `${userForChallan?.surname || ''} ${userForChallan?.firstName || ''} ${userForChallan?.middleName || ''}`.trim() || "वापरकर्ता";
  const mobile = isSociety ? (userForChallan?.chairmanWhatsapp || "9130516001") : (userForChallan?.whatsappNo || "9130516001");
  const address = isSociety ? (userForChallan?.societyAddress || "Samdoli") : (userForChallan?.currentAddress || "Samdoli");

  const workNumbersForPrint = selectedDataFull.map(w => w.workNumber).join(", ");
  const workNumbersWithScheme = selectedDataFull.map(w => `${w.workNumber} (${w.schemeName || ''})`).join(", ");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({ ...prev, [name]: value }));
  };

  const handleCompletePayment = async () => {
    if (!paymentData.userUpiId) { alert("कृपया तुमचा यूपीआय आयडी भरा (Please fill your UPI ID)"); return; }
    setLoading(true);
    try {
      const userKey = userForChallan._key;
      const userNode = userForChallan._node;
      const paymentRef = ref(db, `${userNode}/${userKey}/work_payment/${meetingNumForChallan}`);
      const receiptId = `ZP-WORK-${Math.floor(100000 + Math.random() * 900000)}`;
      const timestamp = new Date().toISOString();
      const paymentPayload = { userUpiId: paymentData.userUpiId, paidAmount: paymentData.paidAmount, userName, mobile, date: timestamp, receiptId, status: 'completed', type: 'Work Selection Fee', meetingNumber: meetingNumForChallan, selectedWorks: selectedDataFull.map(w => `${w.workNumber} - ${w.schemeName || ''}`) };
      await update(paymentRef, paymentPayload);
      setPaymentDone(true);
      setInvoiceData(paymentPayload);
      setTimeout(() => { setPaymentDone(false); setShowPayment(false); setShowInvoice(true); }, 2000);
    } catch (error) { alert("पेमेंट सबमिट करताना त्रुटी आली: " + error.message); } finally { setLoading(false); }
  };

  const handlePrint = () => { window.print(); };

  if (showChallan) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 md:p-8 print:p-0 print:bg-white transition-all duration-500">
        <div className="max-w-5xl mx-auto mb-8 flex justify-between items-center print:hidden">
          <button onClick={() => setShowChallan(false)} className="flex items-center gap-2 px-6 py-3 bg-white text-gray-700 rounded-2xl font-bold shadow-sm hover:bg-gray-50 transition-all border border-gray-200"><X size={20} />मागे फिरा</button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-8 py-3 bg-gray-50 text-gray-700 rounded-2xl font-bold shadow-sm hover:bg-gray-100 transition-all border border-gray-200"><Printer size={20} />चलन प्रिंट करा</button>
        </div>
        <div className="max-w-[1000px] mx-auto bg-white shadow-2xl p-0 md:p-12 print:shadow-none print:p-0 overflow-x-auto font-sans relative">
          <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-blue-50/20 rounded-full blur-3xl -z-10"></div>
          <div className="min-w-[800px] border-[1.5px] border-black text-[12px] leading-tight text-black bg-white">
            <div className="grid grid-cols-2 border-b border-black">
              <div className="p-3 border-r border-black font-bold text-[14px]">Form No.2</div>
              <div className="p-3 font-bold text-right text-[15px] uppercase tracking-tight">CHALAN NO - {challanNo}</div>
            </div>
            <div className="p-3 border-b border-black text-center md:text-left">
              <p className="font-bold text-[13px] mb-1">Chalan of Cash paid into the Sangli Dist. Central Co-Op. Bank Ltd. at</p>
              <p className="font-bold text-[13px]">सांगली जिल्हा मध्यवर्ती सहकारी बँक लि. मध्ये भरलेल्या रोख रकमेचे चलन</p>
            </div>
            <div className="p-3 border-b border-black font-bold text-[13px]">for creadit to the Zilla Parishad सांगली A/C - १०५०५३४००००७७३ with the Head Office</div>
            <div className="grid grid-cols-[1fr_1fr] border-b border-black font-bold text-center">
              <div className="p-2 border-r border-black"><p className="mb-1 text-[13px]">To be filled in by the remitter</p><p className="text-[13px]">रक्कम पाठविणाऱ्याने भरावयाचे</p></div>
              <div className="p-2"><p className="mb-1 text-[13px]">To be filled in by the Dep.Off</p><p className="text-[13px]">खात्याच्या अंमलदाराने भरावयाचे</p></div>
            </div>
            <div className="grid grid-cols-[3fr_1.5fr_2.5fr]">
              <div className="border-r border-black flex flex-col"><div className="p-3 flex-grow min-h-[140px] space-y-4"><div><p className="font-bold text-[13px]">By whom tendered (Name)</p><p className="font-medium">रक्कम कोणी आणून दिली त्याचे नांव</p></div><div><p className="font-bold text-[13px]">Name (or designation ) and address of the person on whose behalf money is paid</p><p className="font-medium">ज्यांच्या वतीने रक्कम भरण्यात आली त्या मनुष्याचे नांव (किंवा हुद्दा ) व पत्ता</p></div></div></div>
              <div className="border-r border-black p-4 flex flex-col justify-center text-center font-bold bg-gray-50/10"><p className="text-[15px] mb-2 uppercase leading-tight">{userName}</p><p className="text-[15px] mb-2">{mobile}</p><p className="text-[14px] leading-snug">{address}</p></div>
              <div className="p-4 space-y-4"><div><p className="font-bold text-[13px]">Account Classification</p><p className="font-bold">Major Head - 0059 <span>सार्वजनिक मालमत्तेपासून उत्पन्न</span></p><p className="font-bold">Minor Head - 800 <span>इतर जमा</span></p><p className="font-bold">Detailed Head - <span>निविदा फॉर्म फी</span></p></div><div className="grid grid-cols-2 mt-8 font-bold"><div><p className="underline">Correct</p><p className="text-[12px]">बरोबर</p></div><div><p>Recived and grant receipts</p><p className="text-[12px]">पैसे घेऊन पावत्या द्याव्यात.</p></div></div><p className="font-bold mt-6 text-[13px]">दिनांक : {today}</p></div>
            </div>
            <div className="grid grid-cols-[3fr_1.5fr_2.5fr] border-t border-black">
              <div className="p-3 border-r border-black min-h-[100px] space-y-4"><p className="font-bold text-[13px]">Full perticulars of the remittances and the authority (if any)</p><p className="font-medium">पाठवलेल्या रकमेचा व अधिकारपत्र त्याचा पूर्ण तपशील</p></div>
              <div className="border-r border-black flex flex-col items-center justify-center p-2 font-bold bg-gray-50/20"><p>Amount</p><p>रक्कम</p></div>
              <div className="p-4 font-bold space-y-2"><p>उप अभियंता (इवड), पंचायत समिती...</p><p>बांधकाम विभाग जि . प . सांगली</p><p>पैशाचा भरणा करणाऱ्याबद्दल फर्मविणाऱ्या अंमलदाराची सही</p></div>
            </div>
            <div className="grid grid-cols-[3fr_1.5fr_2.5fr] border-t border-black">
              <div className="p-4 border-r border-black font-bold text-[13px]">निविदा सूचना क्र . ०१ / २०२५-२६ काम क्र - <span>{workNumbersForPrint || "____"}</span></div>
              <div className="border-r border-black flex items-center justify-center font-bold text-[18px]">250 /-</div>
              <div className="p-4 space-y-4"><p className="font-bold">To be used in only in the case of remittances to Bank</p><p className="font-medium">बँकेत रकमा पाठविण्यांत येतील त्या बाबतीतच.</p></div>
            </div>
            <div className="grid grid-cols-[3fr_1.5fr_2.5fr] border-t border-black min-h-[80px]">
              <div className="p-4 border-r border-black flex flex-col justify-end font-bold"><div><span>Signature</span><span>Total</span></div><div><span>सही</span><span>एकूण</span></div></div>
              <div className="border-r border-black flex items-center justify-center font-black text-[20px]">Rs - 250 /-</div><div className="p-4"></div>
            </div>
            <div className="grid grid-cols-[3fr_4fr] border-t border-black">
              <div className="p-4 border-r border-black font-bold"><p>* In word Rupees - <span>अक्षरी रुपये</span></p></div>
              <div className="p-4 font-bold">Rupees Two Hundred Fifty Only</div>
            </div>
            <div className="grid grid-cols-[1fr_1fr] border-t border-black">
              <div className="p-8 border-r border-black flex flex-col justify-end h-[160px]"><p className="font-bold">Cashier</p></div>
              <div className="flex flex-col h-[160px]"><div className="grid grid-cols-2 border-b border-black h-1/2"><div className="p-3 border-r border-black font-bold"><p className="text-[11px]">Recevied Payment Rupees</p><p className="text-gray-400">Date: / / 202</p></div><div className="p-3 font-bold"><p className="text-[11px]">In words Rupees</p></div></div><div className="p-3 flex items-end h-1/2"><p className="font-bold">Agent</p></div></div>
            </div>
          </div>
          <div className="mt-12 flex justify-center print:hidden border-t pt-12 border-gray-50">
            {isPaid ? (
              <div className="bg-green-50/50 border-2 border-green-200 rounded-[2.5rem] p-10 flex flex-col items-center gap-6 max-w-md w-full shadow-xl">
                <CheckCircle2 size={48} className="text-green-600 animate-pulse" />
                <div className="text-center"><h3 className="text-2xl font-black text-green-800">पेमेंट यशस्वी झाले आहे!</h3></div>
                <button onClick={() => setShowInvoice(true)} className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-black"><Printer size={20} />पावती डाउनलोड करा</button>
              </div>
            ) : (
              <button onClick={() => setShowPayment(true)} className="flex items-center gap-4 px-12 py-5 bg-gradient-to-br from-blue-700 to-indigo-900 text-white rounded-[1.25rem] font-black"><CreditCard size={28} />ऑनलाइन पेमेंट करा</button>
            )}
          </div>
        </div>
        {showPayment && (
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[2000] flex items-center justify-center p-4 print:hidden">
            <div className="bg-white rounded-[3.5rem] w-full max-w-[550px] shadow-2xl relative">
              <div className="bg-gradient-to-b from-blue-700 to-blue-900 p-10 flex flex-col items-center relative">
                <button onClick={() => setShowPayment(false)} className="absolute right-8 top-8 bg-white/20 p-2 rounded-full text-white"><X size={20} /></button>
                <h3 className="text-3xl font-bold text-white">यूपीआय पेमेंट</h3>
              </div>
              <div className="p-10 flex flex-col items-center gap-8">
                {paymentDone ? (
                  <div className="py-12 flex flex-col items-center gap-6"><CheckCircle2 size={48} className="text-green-500 animate-bounce" /><h4 className="text-2xl font-bold">यशस्वी!</h4></div>
                ) : (
                  <>
                    <img src={qrUrl} alt="QR" className="w-[220px] rounded-xl" />
                    <div className="w-full text-center"><label className="text-[11px] font-black uppercase">यूपीआय आयडी</label><input type="text" name="userUpiId" value={paymentData.userUpiId} onChange={handleInputChange} className="w-full text-center text-xl font-bold bg-[#f8f9fa] p-4 rounded-xl" /></div>
                    <button onClick={handleCompletePayment} className="w-full py-5 bg-gradient-to-r from-blue-700 to-indigo-900 text-white rounded-[2rem] font-black">पेमेंट पूर्ण करा</button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {showInvoice && invoiceData && (
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md z-[3000] flex items-center justify-center p-4 overflow-y-auto print:p-0">
            <div className="bg-white rounded-[2.5rem] w-full max-w-2xl shadow-2xl flex flex-col">
              <div className="bg-gradient-to-r from-blue-800 to-indigo-900 p-8 text-white flex justify-between"><div className="flex items-center gap-4"><CreditCard size={32} /><div><h2 className="text-2xl font-black">Payment Receipt</h2></div></div><button onClick={() => setShowInvoice(false)}><X size={20} /></button></div>
              <div className="p-10 space-y-8">
                <div className="flex justify-between"><div><p className="text-[10px] uppercase">Receipt Number</p><p className="text-xl font-bold">{invoiceData.receiptId}</p></div><div><p className="text-[10px] uppercase">Date</p><p>{new Date(invoiceData.date).toLocaleDateString()}</p></div></div>
                <div><p className="text-[10px] uppercase">Paid By</p><p className="text-xl font-bold">{invoiceData.userName}</p></div>
                <div className="bg-gray-50 p-4 rounded-xl"><p>वर्क सिलेक्शन फॉर्म फी</p><p className="text-2xl font-black">₹250.00</p></div>
                <button onClick={handlePrint} className="w-full py-5 bg-gradient-to-r from-blue-700 to-indigo-900 text-white rounded-2xl"><Printer size={24} />Print</button>
              </div>
            </div>
          </div>
        )}
        <style dangerouslySetInnerHTML={{ __html: `@media print { body { background: white !important; } .print\\:hidden { display: none !important; } }` }} />
      </div>
    );
  }

  return (
    <div className="pb-8">
      {/* Breadcrumb / Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-3">
            <ClipboardList className="text-blue-600" />
            <span>इंटरेस्टेड वर्क लिस्ट</span>
          </h1>
          <p className="text-gray-500 font-bold mt-1 uppercase text-xs tracking-widest">सांगली जिल्हा परिषद / Work List</p>
        </div>

        {/* Selection Counter Badge */}
        {selectedWorks.length > 0 && (
          <div className="bg-blue-600 text-white px-6 py-2 rounded-full font-black text-sm shadow-xl animate-bounce">
            निवडलेली कामे: {selectedWorks.length} / 3
          </div>
        )}
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-1 md:p-8">

        {/* Notice Bars */}
        <div className="space-y-4 mb-10">
          <div className="bg-[#1a3c34] text-white p-5 rounded-2xl text-center font-bold shadow-lg border-l-8 border-green-500 transform hover:scale-[1.01] transition-all">
            वर्क सिलेक्शन एकदाच होईल. एकदा वर्क सिलेक्ट केल्यानंतर कोणताही बदल होणार नाही. तुम्ही जास्तीत जास्त ३ कामे निवडू शकता.
          </div>
          <div className="bg-[#1a3c34] text-white p-5 rounded-2xl text-center font-bold shadow-lg border-l-8 border-green-500 transform hover:scale-[1.01] transition-all">
            सबमिट वर्क केले की चलन भरणे बंधनकारक राहील तरच लॉटरी मधील सहभाग ग्राह्य धरला जाईल... चलन डाउनलोड करून बँकेत भरल्यानंतर चलन रिसिट अपलोड करणे.
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
            <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">Fetching Available Works...</p>
          </div>
        ) : (
          <div className="space-y-12">
            {hasVisibleWorks ? (
              Object.entries(groupedWorks).map(([meeting, meetingWorks]) => (
                <div key={meeting} className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                  {/* Meeting Header */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-4">
                      <div className="bg-indigo-600 text-white px-8 py-3 rounded-full font-black text-sm shadow-xl shadow-indigo-100 uppercase tracking-widest border-2 border-white flex items-center gap-3">
                        <span>बैठक क्रमांक:</span>
                        <span className="text-xl">{meeting}</span>
                      </div>
                      {userPayments[meeting] && userPayments[meeting].status === 'completed' && (
                        <div className="bg-green-100 text-green-700 px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 border border-green-200">
                          <CheckCircle size={16} />
                          पेमेंट पूर्ण झाले आहे
                        </div>
                      )}
                    </div>

                  </div>

                  {/* Selected Works Summary for Paid Meetings Hidden */}


                  <div className="bg-white rounded-xl shadow-sm border border-gray-300 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse border border-gray-300">
                        <thead>
                          <tr className="bg-white border-b border-gray-300">
                            <th className="px-3 py-4 text-center font-bold text-gray-800 border-r border-gray-300 w-12">#</th>
                            <th className="px-4 py-4 text-left font-bold text-gray-800 border-r border-gray-300 w-24">वर्क नंबर</th>
                            <th className="px-2 py-4 text-center border-r border-gray-300 w-12 text-blue-600">निवडा</th>
                            <th className="px-4 py-4 text-left font-bold text-gray-800 border-r border-gray-300 w-32 whitespace-nowrap">योजना नाव</th>
                            <th className="px-4 py-4 text-left font-bold text-gray-800 border-r border-gray-300">कामाचे नाव</th>
                            <th className="px-4 py-4 text-left font-bold text-gray-800 border-r border-gray-300 w-36 whitespace-nowrap">निविदा रक्कम (Lakh)</th>
                            <th className="px-4 py-4 text-left font-bold text-gray-800 w-32">तालुका</th>
                          </tr>
                        </thead>
                        <tbody>
                          {meetingWorks.map((work, index) => {
                            const isSubmitted = userPayments[meeting]?.status === 'selected' || userPayments[meeting]?.status === 'completed' || !!userPayments[meeting]?.selectedWorks;
                            const isSelectedInPayment = (userPayments[meeting]?.selectedWorks || []).some(wStr => wStr.startsWith(`${work.workNumber} -`));
                            const isCurrentlySelected = !isSubmitted && selectedWorks.includes(work.id);

                            return (
                              <tr
                                key={work.id}
                                className={`border-b border-gray-300 transition-colors ${isSelectedInPayment
                                  ? 'bg-green-50/80 border-l-4 border-l-green-600'
                                  : isCurrentlySelected
                                    ? 'bg-blue-50/50'
                                    : 'hover:bg-gray-50/50'
                                  }`}
                              >
                                <td className="px-3 py-6 text-center font-medium text-gray-900 border-r border-gray-300 text-base">{index + 1}</td>
                                <td className="px-4 py-6 text-base font-bold text-gray-900 border-r border-gray-300">{work.workNumber}</td>
                                <td className="px-2 py-6 text-center border-r border-gray-300">
                                  <input
                                    type="checkbox"
                                    className={`w-6 h-6 rounded-lg border-gray-400 text-blue-600 focus:ring-blue-500 cursor-pointer shadow-sm ${isSubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    checked={isSelectedInPayment || isCurrentlySelected}
                                    onChange={() => !isSubmitted && handleWorkToggle(work.id)}
                                    disabled={isSubmitted}
                                  />
                                </td>
                                <td className="px-4 py-6 border-r border-gray-300">
                                  <div className="text-xs font-bold text-indigo-600">{work.schemeName || "-"}</div>
                                </td>
                                <td className="px-4 py-6 border-r border-gray-300">
                                  <div className="text-[15px] font-medium text-gray-800 leading-relaxed">{work.workName}</div>
                                  {isSelectedInPayment && (
                                    <span className="inline-block mt-2 px-3 py-1 bg-green-600 text-white text-[10px] font-black rounded-full uppercase tracking-tighter">निवडलेले काम (Selected)</span>
                                  )}
                                </td>
                                <td className="px-4 py-6 border-r border-gray-300 text-base font-bold text-gray-900">
                                  {work.tenderAmount}
                                </td>
                                <td className="px-4 py-6 text-base font-medium text-gray-800">{work.taluka}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-24 text-center">
                <div className="bg-rose-50 text-rose-600 p-8 rounded-3xl inline-block border-2 border-rose-100 shadow-xl shadow-rose-50">
                  <p className="font-black text-xl uppercase tracking-tighter">कोणतीही कामे उपलब्ध नाहीत.</p>
                  <p className="text-rose-400 text-xs font-bold mt-2 italic">कामामध्ये इंटरेस्ट दाखवण्याची वेळ संपली आहे किंवा अद्याप सुरू झालेली नाही.</p>
                </div>
              </div>
            )}

            {/* Submit Action (Planned) */}
            {hasVisibleWorks && selectedWorks.length > 0 && !['selected', 'completed'].includes(userPayments[works.find(w => selectedWorks.includes(w.id))?.meetingNumber]?.status) && (
              <div className="flex justify-center pt-10">
                <button
                  className={`bg-green-600 hover:bg-green-700 text-white px-12 py-4 rounded-2xl font-black text-xl shadow-2xl shadow-green-100 transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3 ${selectedWorks.length === 0 ? 'opacity-70 cursor-not-allowed' : ''}`}
                  onClick={() => {
                    if (selectedWorks.length === 0) {
                      alert("कृपया किमान १ काम निवडा! (Please select at least 1 work!)");
                      return;
                    }
                    setShowConfirmModal(true);
                  }}
                >
                  <CheckCircle size={28} />
                  <span>Submit Works</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1000] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in duration-300 border border-gray-100">
            <div className="bg-[#1a3c34] p-8 text-white text-center relative">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="absolute right-6 top-6 hover:bg-white/20 p-2 rounded-full transition-colors"
                title="बंद करा"
              >
                <X size={20} />
              </button>
              <div className="inline-block p-4 bg-white/10 rounded-full mb-4">
                <AlertCircle size={40} className="text-yellow-400" />
              </div>
              <h3 className="text-2xl font-black tracking-tight">निवडलेली कामे निश्चित करा</h3>
              <p className="text-green-100/70 font-bold text-xs uppercase tracking-widest mt-2">Work Selection Confirmation</p>
            </div>

            <div className="p-10">
              <p className="text-gray-600 font-bold mb-6 text-center">तुम्ही खालील {selectedWorks.length} कामे निवडली आहेत:</p>

              <div className="space-y-4 mb-10">
                {works.filter(w => selectedWorks.includes(w.id)).map((work, idx) => (
                  <div key={work.id} className="flex items-center gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-black">
                      {idx + 1}
                    </div>
                    <div>
                      <p className="text-sm text-indigo-600 font-black uppercase tracking-wider mb-0.5">{work.schemeName || "योजना नाव नाही"}</p>
                      <p className="text-base text-gray-900 font-bold uppercase tracking-wider">वर्क नंबर: {work.workNumber}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-4">
                <button
                  onClick={handleConfirmSubmit}
                  className="w-full py-5 bg-green-600 hover:bg-green-700 text-white rounded-[1.25rem] font-black text-xl shadow-xl shadow-green-100 transition-all transform hover:-translate-y-1 active:scale-95"
                >
                  हो, ही कामे निश्चित आहेत
                </button>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="w-full py-4 text-gray-400 font-bold hover:text-gray-600 transition-colors uppercase tracking-widest text-xs"
                >
                  नाही, बदल करायचा आहे
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
